import {makeAutoObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {NotificationEvent} from '../../../../../api/event';
import type {RenderContract} from '../../NotificationsListItemDumb/types';
import type {
  AsyncEventDataProvider,
  EventContextsMap,
  EventData,
} from '../types';

import {membershipStore} from '../../../../../api/membership';
import {isCourseRequestRefundExpired} from '../../../../../utils/entity/course';
import {isMeCoachForEntity} from '../../../../../utils/entity/isMeCoachForEntity';
import {isEditionPaymentPlanInternal} from '../../../../../utils/entity/program';
import {isDefined} from '../../../../../utils/typecheck';

export class AsyncEventStore implements AsyncEventDataProvider {
  constructor(
    public event: NotificationEvent,
    public contexts: EventContextsMap,
    public staticData: RenderContract,
  ) {
    makeAutoObservable(
      this,
      {
        contexts: observable.ref,
        event: observable.ref,
        staticData: observable.ref,
      },
      {
        autoBind: true,
      },
    );
  }

  get data() {
    return {...this.staticData, buttons: this.getButtons()};
  }

  private getButtons() {
    const {buttons = []} = this.staticData;

    const updatedButtons: EventData['buttons'] = buttons
      .filter(({id}) => id !== 'request_refund_button')
      .map(button => ({...button, scheme: 'primary'}));

    if (this.shouldShowRequestRefundButton()) {
      updatedButtons.unshift({
        id: 'request_refund_button',
        label: i18n.t('shared:request_refund'),
      });
    }

    if (updatedButtons.length > 1) {
      updatedButtons[updatedButtons.length - 1]!.scheme = 'gray';
    }

    return updatedButtons;
  }

  private shouldShowRequestRefundButton() {
    const {course} = this.contexts;

    if (isMeCoachForEntity(course)) {
      return false;
    }

    const membership = course
      ? membershipStore.membershipLookup.get(course._id)
      : null;
    const isRefundRequested = Boolean(membership?.requested_refund);

    return (
      !isRefundRequested &&
      isDefined(course) &&
      isEditionPaymentPlanInternal(course.edition) &&
      !isCourseRequestRefundExpired(course)
    );
  }
}
